import { useState, useEffect } from 'react'
import { Link } from 'wouter'
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  CircleCheck,
  CircleHelp,
  ClipboardList,
  FileText,
  LockKeyhole,
  Send,
} from 'lucide-react'
import { api } from '../services/api'
import type { ConsultaSubmissionData, SiteContentMap } from '../services/api'
import { challenges, schedule, resolveAviso, defaultMensagemEncerramento } from './Home'
import { AvisoBanner } from '../components/AvisoBanner'

const TRL_OPTIONS = [
  { value: 'TRL 1', label: 'TRL 1 — Pesquisa básica', description: 'Princípios básicos observados e reportados' },
  { value: 'TRL 2', label: 'TRL 2 — Formulação tecnológica', description: 'Conceito e/ou aplicação tecnológica formulada' },
  { value: 'TRL 3', label: 'TRL 3 — Prova de conceito', description: 'Função crítica analítica e experimentalmente demonstrada' },
  { value: 'TRL 4', label: 'TRL 4 — Validação em laboratório', description: 'Componente validado em ambiente de laboratório' },
  { value: 'TRL 5', label: 'TRL 5 — Validação em ambiente relevante', description: 'Componente validado em ambiente relevante' },
  { value: 'TRL 6', label: 'TRL 6 — Protótipo demonstrado', description: 'Protótipo demonstrado em ambiente relevante' },
  { value: 'TRL 7', label: 'TRL 7 — Demonstração em ambiente operacional', description: 'Protótipo demonstrado em ambiente operacional' },
  { value: 'TRL 8', label: 'TRL 8 — Sistema completo qualificado', description: 'Sistema real completo e qualificado por testes' },
  { value: 'TRL 9', label: 'TRL 9 — No mercado', description: 'Sistema real comprovado em ambiente operacional' },
]

const PERFIL_OPTIONS = [
  'Startup / Empresa de base tecnológica',
  'Empresa de médio ou grande porte',
  'Universidade / Instituto de pesquisa',
  'Cooperativa / Organização social',
  'Profissional autônomo / Consultor',
  'Servidor público',
  'Cidadão / Sociedade civil',
  'Outro',
]

const documents = [
  { title: 'Edital de Consulta Pública ao Mercado', description: 'Minuta-base para coleta de informações técnicas sobre desafios estratégicos de inovação pública.', status: 'Anexo' },
  { title: 'Anexo I — Descritivo dos Desafios', description: 'Contexto, características mínimas, resultados esperados, beneficiários e limitações de cada desafio.', status: 'Anexo' },
  { title: 'Anexo II — Formulário de Contribuição', description: 'Campos sugeridos para identificação, solução, TRL, investimento, prazo, indicadores e observações.', status: 'Anexo' },
  { title: 'Relatório de Consolidação', description: 'Documento a ser elaborado após a análise das contribuições válidas pela comissão técnica.', status: 'Futuro' },
]

const contributionQuestions = [
  'Soluções disponíveis no mercado para o problema descrito.',
  'Trilhas tecnológicas, funcionalidades e estágio de maturidade, incluindo TRL quando aplicável.',
  'Requisitos mínimos de capacidade técnica, profissional e operacional.',
  'Etapas, tempo estimado, testes em ambiente controlado e cronograma físico-financeiro.',
  'Custos estimados, modalidades de remuneração e indicadores de impacto.',
  'Arranjos de propriedade intelectual, dados, software, código-fonte e licenciamento.',
]

const privacyNotices = [
  { title: 'LGPD', text: 'O tratamento de dados pessoais deverá observar a Lei Geral de Proteção de Dados e as normas aplicáveis à proteção de informações pessoais.' },
  { title: 'Informações sigilosas', text: 'Contribuições poderão ser tornadas públicas, ressalvadas informações identificadas como sigilosas ou protegidas por propriedade intelectual.' },
  { title: 'Caráter não vinculante', text: 'A consulta não obriga a Administração a contratar e não confere exclusividade, vantagem ou preferência futura.' },
  { title: 'Relatório final', text: 'As manifestações poderão ser consolidadas em relatório final para subsidiar termos de referência, editais e minutas futuras.' },
]

const initialForm: ConsultaSubmissionData = {
  tipo: 'contribuicao',
  nome: '',
  cpfCnpj: '',
  email: '',
  telefone: '',
  organizacao: '',
  perfil: '',
  desafio: '',
  trl: '',
  trlDescricao: '',
  contribuicao: '',
  justificativa: '',
  riscosRequisitos: '',
  custos: '',
  melhoriaDesafio: '',
  comentariosExtras: '',
  consentimento: false,
}

export default function ConsultaPublica() {
  const [formData, setFormData] = useState<ConsultaSubmissionData>(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [showTrlHelp, setShowTrlHelp] = useState(false)
  const [content, setContent] = useState<SiteContentMap>({})

  useEffect(() => {
    api.getContent().then(setContent)
  }, [])

  const displayChallenges = challenges.map((challenge) => {
    const override = content.challenges?.find((c) => c.id === challenge.id)
    return override ? { ...challenge, title: override.title, question: override.question, result: override.result } : challenge
  })
  const displaySchedule = content.schedule && content.schedule.length > 0 ? content.schedule : schedule
  const inscricoesEncerradas = content.inscricoes?.aberta === false

  function updateField(field: keyof ConsultaSubmissionData, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError('')

    if (!formData.nome?.trim()) {
      setError('Informe seu nome ou razão social.')
      return
    }
    if (!formData.email?.trim()) {
      setError('Informe um e-mail para contato.')
      return
    }
    if (!formData.desafio) {
      setError('Selecione o desafio ao qual deseja contribuir.')
      return
    }
    if (!formData.contribuicao.trim()) {
      setError('Descreva sua contribuição técnica.')
      return
    }
    if (!formData.consentimento) {
      setError('É necessário aceitar os termos para enviar.')
      return
    }

    const trlOption = TRL_OPTIONS.find((t) => t.value === formData.trl)
    setSubmitting(true)
    try {
      await api.submitConsulta({ ...formData, trlDescricao: trlOption?.description || '' })
      setSubmitted(true)
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar contribuição.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9f2] text-[#102334]">
      <AvisoBanner aviso={resolveAviso(content)} />
      <section className="relative isolate overflow-hidden border-b border-[#d8e6cf] bg-[#0b2e46] py-20 text-white">
        <div className="container relative">
          <Link href="/" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#b9e28f] hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Voltar ao site institucional
          </Link>
          <div className="mt-12 grid gap-12 lg:grid-cols-[0.82fr_0.54fr]">
            <div>
              <p className="font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#f1b90f]">Fase de consulta pública</p>
              <h1 className="mt-5 max-w-4xl font-serif text-4xl font-black leading-[0.98] tracking-[-0.045em] sm:text-5xl">
                Consulta Pública ao Mercado — CPSI
              </h1>
              <p className="mt-7 max-w-3xl text-xl leading-9 text-white/82">
                Espaço para apresentação da minuta, dos desafios estratégicos e das orientações de
                participação na fase preparatória de futuras Contratações Públicas de Solução Inovadora
                em Porto Velho.
              </p>
            </div>
            <aside className="border-l-4 border-[#f1b90f] bg-white/10 p-7 backdrop-blur">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#b9e28f]">Resumo executivo</p>
              <div className="mt-6 grid gap-4">
                <Badge label="Status" value="Em andamento" />
                <Badge label="Período" value={`${displaySchedule[0]?.date} a ${displaySchedule[1]?.date.split(' a ')[1] || displaySchedule[displaySchedule.length - 1]?.date}`} />
                <Badge label="Natureza" value="Consultiva" />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container grid gap-10 lg:grid-cols-[0.86fr_1.14fr]">
          <div>
            <p className="section-kicker">Objetivo da consulta</p>
            <h2 className="section-title">Receber informações e contribuições técnicas.</h2>
            <p className="mt-6 text-lg leading-8 text-[#455c62]">
              A consulta busca subsidiar a estruturação de futuros editais de CPSI, reduzir assimetria de
              informações e ampliar o conhecimento da Administração sobre soluções inovadoras disponíveis.
            </p>
            <div className="mt-8 border-l-4 border-[#f1b90f] bg-[#fff8dd] p-5 text-sm font-semibold leading-7 text-[#735a00]">
              A participação não gera remuneração, indenização, exclusividade, vantagem ou preferência em
              eventual contratação futura.
            </div>
          </div>
          <div className="grid gap-4">
            {displaySchedule.map((item) => (
              <article key={`${item.date}-${item.stage}`} className="grid gap-4 border border-[#d8e6cf] bg-white p-5 shadow-sm md:grid-cols-[170px_1fr]">
                <div className="flex items-center gap-3 font-mono text-sm font-black text-[#0d6b3d]">
                  <CalendarDays className="h-5 w-5" />
                  {item.date}
                </div>
                <div>
                  <h3 className="font-serif text-2xl font-black text-[#0d2d47]">{item.stage}</h3>
                  <p className="mt-2 leading-7 text-[#52676e]">{item.note}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="documentos" className="bg-[#eaf3e2] py-20">
        <div className="container">
          <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr]">
            <div>
              <p className="section-kicker">Documentos e anexos</p>
              <h2 className="section-title">Material de referência para manifestação técnica.</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {documents.map((doc) => (
                <article key={doc.title} className="border border-[#d8e6cf] bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <FileText className="h-7 w-7 text-[#0d6b3d]" />
                    <span className="bg-[#f4f8ee] px-3 py-1 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#6a7a48]">
                      {doc.status}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-black text-[#0d2d47]">{doc.title}</h3>
                  <p className="mt-3 leading-7 text-[#52676e]">{doc.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container">
          <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
            <div>
              <p className="section-kicker">Desafios em consulta</p>
              <h2 className="section-title">Contribuições devem responder aos problemas públicos definidos.</h2>
            </div>
            <p className="text-lg leading-8 text-[#52676e]">
              Envie informações técnicas, referências de mercado, riscos, requisitos, indicadores e sugestões
              de aprimoramento para qualificação do desafio.
            </p>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {displayChallenges.map((challenge) => (
              <article key={challenge.id} className="border border-gray-100 bg-white p-6 shadow-sm">
                {challenge.icon}
                <p className="mt-5 font-mono text-xs font-bold uppercase tracking-[0.16em] text-[#6a7a48]">
                  Desafio {challenge.id}
                </p>
                <h3 className="mt-2 font-serif text-2xl font-black text-[#0d2d47]">{challenge.title}</h3>
                <p className="mt-4 leading-7 text-[#455c62]">{challenge.question}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="formulario" className="bg-[#0b2e46] py-20 text-white">
        <div className="container grid gap-10 lg:grid-cols-[0.68fr_1.32fr]">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#b9e28f]">Formulário de contribuição</p>
            <h2 className="mt-4 font-serif text-4xl font-black tracking-[-0.035em]">Como estruturar a contribuição.</h2>
            <p className="mt-5 leading-8 text-white/78">
              Poderão contribuir pessoas físicas e jurídicas, de direito público ou privado, nacionais ou
              estrangeiras, de forma individual ou consorciada. As contribuições podem abordar um ou mais
              desafios, sempre com foco em informações úteis para a Administração estruturar eventual
              contratação futura.
            </p>
            <div className="mt-8 grid gap-3">
              {contributionQuestions.map((question) => (
                <div key={question} className="flex items-start gap-3 border border-white/12 bg-white/[0.06] p-4">
                  <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#f1b90f]" />
                  <span className="leading-7 text-white/84">{question}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-7 text-[#102334] shadow-[0_30px_90px_rgba(0,0,0,0.22)]">
            {inscricoesEncerradas ? (
              <div className="flex flex-col items-center gap-6 py-12 text-center">
                <LockKeyhole className="h-16 w-16 text-[#0d2d47]" />
                <h3 className="font-serif text-3xl font-black text-[#0d2d47]">Inscrições encerradas</h3>
                <p className="max-w-md text-lg leading-8 text-[#52676e]">
                  {content.inscricoes?.mensagem?.trim() || defaultMensagemEncerramento}
                </p>
              </div>
            ) : submitted ? (
              <div className="flex flex-col items-center gap-6 py-12 text-center">
                <CircleCheck className="h-16 w-16 text-[#0d6b3d]" />
                <h3 className="font-serif text-3xl font-black text-[#0d2d47]">Contribuição enviada</h3>
                <p className="max-w-md text-lg leading-8 text-[#52676e]">
                  Sua manifestação foi registrada com sucesso. Agradecemos pela participação na consulta pública.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false)
                    setFormData(initialForm)
                  }}
                  className="mt-4 bg-[#0d6b3d] px-6 py-3 font-bold text-white hover:bg-[#0b5b35]"
                >
                  Enviar nova contribuição
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-4 border-b border-[#dfead7] pb-5">
                  <ClipboardList className="h-8 w-8 text-[#0d6b3d]" />
                  <div>
                    <h3 className="font-serif text-3xl font-black text-[#0d2d47]">Formulário de contribuição</h3>
                    <p className="mt-2 text-sm leading-6 text-[#52676e]">
                      Preencha os campos abaixo. Sua contribuição será registrada e ficará disponível para
                      análise da comissão técnica.
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mt-5 border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                    {error}
                  </div>
                )}

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Identificação do proponente
                  </legend>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormInput label="Nome completo / Razão social *" value={formData.nome || ''} onChange={(v) => updateField('nome', v)} placeholder="Nome ou razão social" required />
                    <FormInput label="CPF ou CNPJ" value={formData.cpfCnpj || ''} onChange={(v) => updateField('cpfCnpj', v)} placeholder="000.000.000-00 ou 00.000.000/0000-00" />
                    <FormInput label="E-mail *" type="email" value={formData.email || ''} onChange={(v) => updateField('email', v)} placeholder="contato@exemplo.com.br" required />
                    <FormInput label="Telefone" value={formData.telefone || ''} onChange={(v) => updateField('telefone', v)} placeholder="(00) 00000-0000" />
                    <FormInput label="Organização / Instituição" value={formData.organizacao || ''} onChange={(v) => updateField('organizacao', v)} placeholder="Nome da empresa, universidade, etc." />
                    <FormSelect label="Perfil do respondente" value={formData.perfil || ''} onChange={(v) => updateField('perfil', v)} options={PERFIL_OPTIONS.map((p) => ({ value: p, label: p }))} placeholder="Selecione seu perfil" />
                  </div>
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Desafio selecionado
                  </legend>
                  <FormSelect
                    label="Para qual desafio deseja contribuir? *"
                    value={formData.desafio || ''}
                    onChange={(v) => updateField('desafio', v)}
                    options={displayChallenges.map((c) => ({ value: `Desafio ${c.id} — ${c.title}`, label: `Desafio ${c.id} — ${c.title}` }))}
                    placeholder="Selecione o desafio"
                    required
                  />
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d] flex items-center">
                    Nível de maturidade da solução
                    <button type="button" onClick={() => setShowTrlHelp(!showTrlHelp)} className="ml-2 inline-flex items-center text-[#52676e] hover:text-[#0d6b3d]">
                      <CircleHelp className="h-4 w-4" />
                    </button>
                  </legend>
                  {showTrlHelp && (
                    <div className="mb-4 border border-[#d8e6cf] bg-[#f4f8ee] p-4 text-sm leading-7 text-[#455c62]">
                      <p className="mb-2 font-bold text-[#0d2d47]">O que é TRL (Technology Readiness Level)?</p>
                      <p>
                        É uma escala de 1 a 9 que mede o grau de maturidade de uma tecnologia ou solução.
                        Selecione o nível que melhor representa o estágio atual da sua proposta:
                      </p>
                      <ul className="mt-3 grid gap-1">
                        <li><span className="font-bold text-[#0d6b3d]">TRL 1-3:</span> Fase de pesquisa (conceito, prova de conceito)</li>
                        <li><span className="font-bold text-[#f1b90f]">TRL 4-6:</span> Em desenvolvimento (validação, protótipo)</li>
                        <li><span className="font-bold text-[#0b2e46]">TRL 7-9:</span> No mercado (demonstração operacional, produto comercial)</li>
                      </ul>
                    </div>
                  )}
                  <FormSelect
                    label="Selecione o nível de maturidade (TRL)"
                    value={formData.trl || ''}
                    onChange={(v) => updateField('trl', v)}
                    options={TRL_OPTIONS.map((t) => ({ value: t.value, label: t.label }))}
                    placeholder="Selecione o TRL da sua solução"
                  />
                  {formData.trl && (
                    <p className="mt-2 text-sm italic text-[#52676e]">
                      {TRL_OPTIONS.find((t) => t.value === formData.trl)?.description}
                    </p>
                  )}
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Contribuição técnica
                  </legend>
                  <FormTextarea label="Resumo da solução ou contribuição *" value={formData.contribuicao} onChange={(v) => updateField('contribuicao', v)} placeholder="Descreva a solução proposta, funcionalidades, diferenciais e como ela responde ao desafio selecionado." required />
                  <FormTextarea label="Justificativa e fundamentação" value={formData.justificativa || ''} onChange={(v) => updateField('justificativa', v)} placeholder="Explique por que esta solução é adequada, referências de mercado, casos de sucesso, etc." />
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Riscos, requisitos e indicadores
                  </legend>
                  <FormTextarea label="Riscos identificados, requisitos técnicos e indicadores de impacto" value={formData.riscosRequisitos || ''} onChange={(v) => updateField('riscosRequisitos', v)} placeholder="Descreva riscos potenciais, requisitos mínimos de infraestrutura/capacidade técnica, e indicadores que podem medir o sucesso da solução." />
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Custos estimados
                  </legend>
                  <FormTextarea label="Estimativa de custos, modalidades de remuneração e cronograma financeiro" value={formData.custos || ''} onChange={(v) => updateField('custos', v)} placeholder="Informe custos estimados de implantação, operação, manutenção, modalidades de remuneração sugeridas e cronograma físico-financeiro." />
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Sugestões de melhoria do desafio
                  </legend>
                  <FormTextarea label="Contribuições e sugestões para qualificar ou reestruturar o desafio" value={formData.melhoriaDesafio || ''} onChange={(v) => updateField('melhoriaDesafio', v)} placeholder="Caso identifique oportunidades de melhoria na formulação do desafio, requisitos, escopo ou critérios, descreva aqui suas sugestões." />
                </fieldset>

                <fieldset className="mt-6 border border-[#d8e6cf] p-5">
                  <legend className="bg-white px-3 font-mono text-xs font-black uppercase tracking-[0.14em] text-[#0d6b3d]">
                    Comentários e sugestões adicionais
                  </legend>
                  <FormTextarea label="Campo livre para pontos extras não contemplados nas perguntas anteriores" value={formData.comentariosExtras || ''} onChange={(v) => updateField('comentariosExtras', v)} placeholder="Utilize este espaço para trazer informações, observações ou sugestões que não se encaixam nos campos anteriores." />
                </fieldset>

                <div className="mt-6 border-t border-[#dfead7] pt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consentimento}
                      onChange={(e) => updateField('consentimento', e.target.checked)}
                      className="mt-1 h-5 w-5 accent-[#0d6b3d]"
                    />
                    <span className="text-sm leading-6 text-[#455c62]">
                      Declaro que as informações prestadas são verdadeiras, autorizo o tratamento dos dados
                      pessoais conforme a LGPD e estou ciente de que a participação não gera direito à
                      contratação, remuneração, exclusividade ou preferência em processos futuros.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 inline-flex items-center gap-3 bg-[#0d6b3d] px-7 py-4 font-bold text-white transition hover:bg-[#0b5b35] disabled:opacity-50"
                >
                  <Send className="h-5 w-5" />
                  {submitting ? 'Enviando...' : 'Enviar contribuição'}
                </button>
              </>
            )}
          </form>
        </div>
      </section>

      <section className="py-20">
        <div className="container grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {privacyNotices.map((item) => (
            <article key={item.title} className="border border-[#d8e6cf] bg-white p-6 shadow-sm">
              <LockKeyhole className="h-6 w-6 text-[#0d6b3d]" />
              <h3 className="mt-4 font-serif text-xl font-black text-[#0d2d47]">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-[#52676e]">{item.text}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function Badge({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-white/12 bg-white/[0.06] p-4">
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#b9e28f]">{label}</p>
      <p className="mt-1 font-serif text-2xl font-black">{value}</p>
    </div>
  )
}

function FormInput({
  label,
  value,
  onChange,
  placeholder = '',
  type = 'text',
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="font-mono text-xs font-black uppercase tracking-[0.14em] text-[#5d7f48]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-[#cbdcc2] bg-[#fbfdf8] px-4 py-3 outline-none transition focus:border-[#0d6b3d] focus:ring-4 focus:ring-[#69b943]/20"
        placeholder={placeholder}
        required={required}
      />
    </label>
  )
}

function FormSelect({
  label,
  value,
  onChange,
  options,
  placeholder = '',
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="font-mono text-xs font-black uppercase tracking-[0.14em] text-[#5d7f48]">{label}</span>
      <div className="relative mt-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none border border-[#cbdcc2] bg-[#fbfdf8] px-4 py-3 pr-10 outline-none transition focus:border-[#0d6b3d] focus:ring-4 focus:ring-[#69b943]/20"
          required={required}
        >
          <option value="">{placeholder || 'Selecione...'}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6a7a48]" />
      </div>
    </label>
  )
}

function FormTextarea({
  label,
  value,
  onChange,
  placeholder = '',
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="mt-4 block">
      <span className="font-mono text-xs font-black uppercase tracking-[0.14em] text-[#5d7f48]">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 min-h-32 w-full border border-[#cbdcc2] bg-[#fbfdf8] px-4 py-3 outline-none transition focus:border-[#0d6b3d] focus:ring-4 focus:ring-[#69b943]/20"
        placeholder={placeholder}
        required={required}
      />
    </label>
  )
}
